import React, { FC } from 'react';
import styled from '@emotion/styled';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useI18n, useLogger, usePrinterApi, useDispatch } from '~Hooks';
import * as actions from '~actions';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box } from '~Styles';
import { CircleSpinner } from 'react-spinners-kit';
import { handyBreakpoint } from '~Styles/breakpoints';

const CLOUD_PRINT_JOBS_QUERY = gql`
  query GetCloudPrintJobs($venueId: String!) {
    operativeViewer {
      id
      cloudPrintJobs(venueId: $venueId) {
        id
        printerId
        venueId
        status
        mediaType
        printType
        orderId
        userId
        createdAt
        pickedUpAt
        completedAt
        resultCode
        resultMessage
      }
    }
  }
`;

const REPRINT_MUTATION = gql`
  mutation ReprintCloudJob($jobId: String!) {
    reprintCloudJob(jobId: $jobId) {
      success
      message
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 24px 32px;
  box-sizing: border-box;

  ${handyBreakpoint} {
    padding: 0;
  }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 16px;
`;

const Row = styled.div<{ isHeader?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 100px 1fr 1fr 80px;
  gap: 8px;
  padding: 12px 16px;
  background-color: ${({ isHeader }) => (isHeader ? 'var(--colorBlueGray900)' : '#fff')};
  color: ${({ isHeader }) => (isHeader ? 'var(--colorWhite)' : '#000')};
  border-radius: ${({ isHeader }) => (isHeader ? '4px 4px 0 0' : '0')};
  font-size: 14px;
  align-items: center;
`;

const ReprintButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background-color: var(--colorBlueGray900);
  color: var(--colorWhite);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
  color: #fff;
  background-color: ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'var(--colorUIGreen)';
      case 'picked_up':
        return 'var(--colorUIYellow, #f59e0b)';
      case 'pending':
        return 'var(--colorUIBlue, #3b82f6)';
      case 'error':
        return 'var(--colorUIRed)';
      default:
        return 'var(--colorBlueGray600)';
    }
  }};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 16px;
`;

function formatTime(isoString: string | null): string {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * Calculates the duration between two ISO timestamps in a human-readable format.
 * Returns null if either timestamp is missing.
 */
function getDuration(start: string | null, end: string | null): string | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

interface CloudPrintJob {
  id: string;
  printerId: string;
  venueId: string;
  status: string;
  mediaType: string;
  printType: string | null;
  orderId: string | null;
  userId: string | null;
  createdAt: string;
  pickedUpAt: string | null;
  completedAt: string | null;
  resultCode: string | null;
  resultMessage: string | null;
}

const CloudPrintHistory: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('cloud-print-history');
  const printerApi = usePrinterApi();
  const dispatch = useDispatch();
  const [, , venueId] = location.hash.split('/');

  const jobsQuery = useQuery(CLOUD_PRINT_JOBS_QUERY, {
    variables: { venueId },
    pollInterval: 3000,
    fetchPolicy: 'network-only',
  });

  const [reprintJob, reprintMutation] = useMutation(REPRINT_MUTATION, {
    refetchQueries: ['GetCloudPrintJobs'],
  });

  async function handleReprint(jobId: string) {
    try {
      logger.info('Reprinting cloud job', { jobId });
      await reprintJob({ variables: { jobId } });
      dispatch(actions.addToast('Reprint queued', true));
    } catch (err) {
      logger.error('Failed to reprint cloud job', { jobId });
      dispatch(actions.addToast('Reprint failed', false));
    }
  }

  const jobs: CloudPrintJob[] = jobsQuery.data?.operativeViewer?.cloudPrintJobs ?? [];

  if (jobsQuery.loading && !jobsQuery.data) {
    return (
      <Wrapper>
        <Box display="flex" justifyContent="center" padding="xl">
          <CircleSpinner size={32} color="#fff" />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Heading level={1} color="#fff">
        Cloud Print History
      </Heading>
      <Paragraph level={2} color="var(--colorBlueGray200)" top={8}>
        Last 24 hours of cloud print jobs for this venue
      </Paragraph>

      {jobs.length === 0 ? (
        <EmptyState>
          <Paragraph level={2} color="var(--colorBlueGray400)">
            No cloud print jobs in the last 24 hours
          </Paragraph>
        </EmptyState>
      ) : (
        <Table>
          <Row isHeader>
            <span>Time</span>
            <span>Type</span>
            <span>Printer</span>
            <span>Status</span>
            <span>Duration</span>
            <span>Result</span>
            <span></span>
          </Row>
          {jobs.map((job) => {
            const printerName = printerApi.getNameFromId(job.printerId) ?? job.printerId;
            const duration = getDuration(job.createdAt, job.completedAt ?? job.pickedUpAt);

            return (
              <Row key={job.id}>
                <span>
                  {formatDate(job.createdAt)} {formatTime(job.createdAt)}
                </span>
                <span>{job.printType ?? '-'}</span>
                <span title={job.printerId}>{printerName}</span>
                <span>
                  <StatusBadge status={job.status}>{job.status.toUpperCase()}</StatusBadge>
                </span>
                <span>{duration ?? '-'}</span>
                <span>
                  {job.resultCode && job.resultMessage
                    ? `${job.resultCode}: ${job.resultMessage}`
                    : job.resultCode
                    ? job.resultCode
                    : '-'}
                </span>
                <span>
                  <ReprintButton
                    onClick={() => handleReprint(job.id)}
                    disabled={reprintMutation.loading}
                  >
                    Reprint
                  </ReprintButton>
                </span>
              </Row>
            );
          })}
        </Table>
      )}
    </Wrapper>
  );
};

export default CloudPrintHistory;
